import React from "react";
import { graphql } from "gatsby";
import { Layout } from "~/templates/Layout";
import { DownloadArticleHeader } from "~/molecules/DownloadArticleHeader";
import { DownloadArticleBody } from "~/molecules/DownloadArticleBody";
import { DownloadApplicationForm } from "~/molecules/DownloadApplicationForm";
import { DocumentPreviewSlider } from "~/molecules/DocumentPreviewSlider";
import { RecommendedContentSidebar } from "~/molecules/RecommendedContentSidebar";
import { getDownloadOgpImageUrl, resolveDownloadSliderImages } from "~/utils/downloadPreviewImages";
import { colors } from "~/utils/colors";
import { breadcrumbContentGap, breakPoints, fontFamily } from "~/utils/variables";
import { PATH } from "~/constant/path";
import { useTaggedContentSections } from "~/hooks/useTaggedContentSections";
import { resolveTagSlugs } from "~/utils/recommendedContent";
import "~/graphql/recommendedFragments";

interface Props {
  pageContext: {
    contentfulId: string;
    tagSlugs?: ReadonlyArray<string>;
  };
  path: string;
  data: {
    contentfulDownload: GatsbyTypes.ContentfulDownload;
    recommendCases?: GatsbyTypes.ContentfulCaseConnection;
    recommendSeminars?: GatsbyTypes.ContentfulEventConnection;
    recommendDownloads?: GatsbyTypes.ContentfulDownloadConnection;
    recommendAwards?: GatsbyTypes.ContentfulAwardConnection;
    recommendInsights?: GatsbyTypes.ContentfulInsightArticleConnection;
  };
}

const RedirectableDownload: React.FC<Props> = ({ pageContext, data }) => {
  const download = data?.contentfulDownload;
  const tagSlugs = resolveTagSlugs(pageContext.tagSlugs, download?.tag);
  const recommendedSections = useTaggedContentSections({
    primaryType: "download",
    tagSlugs,
    caseEdges: data?.recommendCases?.edges || [],
    seminarEdges: data?.recommendSeminars?.edges || [],
    downloadEdges: data?.recommendDownloads?.edges || [],
    awardEdges: data?.recommendAwards?.edges || [],
    insightEdges: data?.recommendInsights?.edges || [],
  });
  const hasRecommended = recommendedSections.some((section) => section.items.length > 0);

  if (!download) {
    return (
      <Layout
        title="資料が見つかりません"
        breadCrumbItems={[PATH.TOP, PATH.DOWNLOADS, { name: `資料が見つかりません` }]}
      >
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <p>資料が見つかりませんでした。</p>
        </div>
      </Layout>
    );
  }

  const { title, description, ogpDescription, documentCover, previewImages, hubspotFormId, pathSlug } = download;
  const sharedOgpDescription = ogpDescription?.internal?.content;
  const previewImageList = resolveDownloadSliderImages(previewImages, documentCover);
  const pageTitle = title || "";
  const layoutTitle = pageTitle ? `${pageTitle}｜Refcome（リフカム）` : "";
  const isRequestPage = pathSlug === "request";
  const breadCrumbItems = isRequestPage ? undefined : [PATH.TOP, PATH.DOWNLOADS, { name: pageTitle }];
  const formTargetId = "download-application-form";

  const scrollToForm = () => {
    const formElement = document.getElementById(formTargetId);
    if (!formElement) {
      return;
    }
    formElement.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <style jsx>{`
        .articleWrapper {
          -webkit-font-smoothing: antialiased;
          background-color: ${colors.lightGray};
          font-family: ${fontFamily.insight};
          font-feature-settings: "palt";
          min-height: 80vh;
          padding: 0 0 50px;
          text-rendering: optimizeLegibility;
        }
        .articleWrapper.noBreadcrumb {
          padding-top: ${42 + breadcrumbContentGap}px;
        }
        @media (max-height: 800px) {
          .articleWrapper.noBreadcrumb {
            padding-top: ${34 + 16}px;
          }
        }
        .articleWrapper :global(h1),
        .articleWrapper :global(h2),
        .articleWrapper :global(h3),
        .articleWrapper :global(h4),
        .articleWrapper :global(h5),
        .articleWrapper :global(h6) {
          font-weight: 700;
        }
        .component {
          box-sizing: border-box;
          margin: 0 auto;
          max-width: 1140px;
          padding: 0 24px;
        }
        .body {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin: 0 auto;
          max-width: 100%;
          min-width: 0;
          width: 100%;
        }
        .description {
          display: flex;
          flex-direction: column;
          gap: 20px;
          min-width: 0;
          width: 100%;
        }
        .heroSection {
          padding: 0;
        }
        .previewWrap {
          margin-top: 8px;
          width: 100%;
        }
        .downloadButtonWrapper {
          display: none;
        }
        .downloadButton {
          align-items: center;
          background: ${colors.seminarCtaGradient};
          border: none;
          border-radius: 50px;
          box-sizing: border-box;
          color: ${colors.white};
          cursor: pointer;
          display: flex;
          font-size: 15px;
          font-weight: 700;
          justify-content: center;
          line-height: 1.4;
          padding: 14px 20px;
          text-align: center;
          transition: opacity 0.2s ease-out;
          width: 100%;
        }
        .downloadButton:active {
          opacity: 0.85;
        }
        .bodySection {
          padding: 0;
          width: 100%;
        }
        .sideColumn {
          display: flex;
          flex-direction: column;
          gap: 28px;
          max-width: 100%;
          min-width: 0;
          width: 100%;
        }
        .formBlock {
          width: 100%;
        }
        @media (${breakPoints.minPc}) {
          .body {
            align-items: start;
            display: grid;
            gap: 0 40px;
            grid-template-columns: minmax(0, 500px) 340px;
            justify-content: center;
            width: fit-content;
          }
        }
        @media (${breakPoints.sp}) {
          .articleWrapper {
            padding-bottom: 40px;
          }
          .articleWrapper.noBreadcrumb {
            padding-top: 32px;
          }
          .component {
            padding: 0 20px;
          }
          .description {
            gap: 16px;
          }
          .previewWrap {
            margin-top: 4px;
          }
          .downloadButtonWrapper {
            display: flex;
            justify-content: center;
            margin-top: 16px;
          }
          .downloadButton {
            font-size: 15px;
            padding: 12px 28px;
            width: auto;
          }
        }
      `}</style>
      <Layout
        title={layoutTitle}
        omitSiteName
        ogpDescription={sharedOgpDescription || ""}
        ogpImageUrl={getDownloadOgpImageUrl(previewImages, documentCover)}
        breadCrumbItems={breadCrumbItems}
        hideBreadCrumbs={isRequestPage}
        breadCrumbBgColor={colors.lightGray}
      >
        <div className={`articleWrapper${isRequestPage ? " noBreadcrumb" : ""}`}>
          <div className="component">
            <div className="body">
              <div className="description">
                <section className="heroSection">
                  <DownloadArticleHeader
                    categoryName={pathSlug === "request" ? undefined : "お役立ち資料"}
                    title={pageTitle}
                    tags={download.tag}
                  />
                  {previewImageList.length > 0 && (
                    <div className="previewWrap">
                      <DocumentPreviewSlider embedded images={previewImageList} alt={title || ""} />
                    </div>
                  )}
                  {hubspotFormId && (
                    <div className="downloadButtonWrapper">
                      <button type="button" className="downloadButton" onClick={scrollToForm}>
                        今すぐダウンロードする
                      </button>
                    </div>
                  )}
                </section>
                <section className="bodySection">
                  <DownloadArticleBody description={description} />
                </section>
              </div>
              {(hubspotFormId || hasRecommended) && (
                <div className="sideColumn">
                  {hubspotFormId && (
                    <div className="formBlock">
                      <DownloadApplicationForm hubspotFormId={hubspotFormId} formTargetId={formTargetId} />
                    </div>
                  )}
                  {hasRecommended && <RecommendedContentSidebar sections={recommendedSections} />}
                </div>
              )}
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
};

export default RedirectableDownload;

export const redirectableDownloadQuery = graphql`
  query RedirectableDownload($contentfulId: String) {
    contentfulDownload(contentful_id: { eq: $contentfulId }) {
      pathSlug
      hubspotFormId
      documentCover {
        fluid(maxWidth: 767) {
          src
          aspectRatio
        }
      }
      previewImages {
        fluid(maxWidth: 767) {
          src
          aspectRatio
        }
      }
      description {
        raw
      }
      title
      tag {
        name
        slug
      }
      ogpDescription {
        internal {
          content
        }
      }
    }
    recommendCases: allContentfulCase(
      filter: { contentful_id: { ne: $contentfulId }, tag: { elemMatch: { slug: { ne: null } } } }
      sort: { fields: date, order: DESC }
      limit: 50
    ) {
      edges {
        node {
          ...RecommendedCaseFields
        }
      }
    }
    recommendSeminars: allContentfulEvent(
      filter: { contentful_id: { ne: $contentfulId } }
      sort: { fields: date, order: DESC }
      limit: 50
    ) {
      edges {
        node {
          ...RecommendedSeminarFields
        }
      }
    }
    recommendDownloads: allContentfulDownload(
      filter: { contentful_id: { ne: $contentfulId }, tag: { elemMatch: { slug: { ne: null } } } }
      sort: { fields: createdAt, order: DESC }
      limit: 50
    ) {
      edges {
        node {
          ...RecommendedDownloadFields
        }
      }
    }
    recommendAwards: allContentfulAward(
      filter: { contentful_id: { ne: $contentfulId }, tag: { elemMatch: { slug: { ne: null } } } }
      sort: { fields: publishedAt, order: DESC }
      limit: 50
    ) {
      edges {
        node {
          ...RecommendedAwardFields
        }
      }
    }
    recommendInsights: allContentfulInsightArticle(
      filter: { contentful_id: { ne: $contentfulId }, tag: { elemMatch: { slug: { ne: null } } } }
      sort: { fields: publishedAt, order: DESC }
      limit: 50
    ) {
      edges {
        node {
          ...RecommendedInsightFields
        }
      }
    }
  }
`;
